import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Form } from '@ui';
import { ProfileFormActions } from '@lp/features/Profile/components/ProfileFormActions';
import { getDefaultValues, useUserSchema } from '@lp/features/Profile/config';
import { Profile } from '@lp/features/Profile/Profile';
import { ProfileFormType } from '@lp/features/Profile/types';
import { useSetHomeLocation } from '@lp/core/hooks/useSetHomeLocation';
import { useAuthContext } from '@lp/core/auth';
import { EmployeeFormValues } from '@/types';
import { UserProfileType } from '@/api/apis/user';
import { useUpdateProfileMutation } from '@/queries/employee';
import { useLoaderData } from '@/hooks/useLoaderData';
import { getChangedValues } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { getCountryHasStates } from '@/hooks/useCountrySubdivisionInfo';
import { useUploadFile } from '@/hooks/useUploadFile';
import { FileType } from '@/queries/file';
import { ProfilePermissions } from '@/permissions';

export const ProfilePage = () => {
  const { checkPermission } = useAuthContext();
  const [isReadOnly, setIsReadOnly] = useState(true);
  const userData = useLoaderData<UserProfileType>();
  const { mutateAsync } = useUpdateProfileMutation();
  const setHomeLocation = useSetHomeLocation(userData.employee.homeLocation?.id);
  const { uploadFiles } = useUploadFile(FileType.Resource);
  const hasUpdatePermission = checkPermission(ProfilePermissions.ProfileUpdate);
  const { schema, isLoading: isGeoDataLoading } = useUserSchema();
  const { composedSubdivisionsList } = useCountriesData();

  const handleFormState = () => {
    setIsReadOnly(!isReadOnly);
  };

  const defaultValues = useMemo(() => getDefaultValues(userData.employee), [userData]);

  const handleSubmit = async (data: ProfileFormType) => {
    try {
      let uploadedFile;

      if (data.photoFile) {
        uploadedFile = await uploadFiles([data.photoFile]);
      }

      const { country, addressLine, state, city, postalCode, latitude, longitude, homeLocationTimezone } = data;
      const locationValues = { country, addressLine, state, city, postalCode, latitude, longitude };
      const countryHasStates = getCountryHasStates(composedSubdivisionsList, country);
      const updateBody = {
        profilePhoto: uploadedFile ? uploadedFile[0].fileName : data.profilePhoto,
        roles: userData.employee.roles,
        phone: data.phone,
        hireDate: data.hireDate,
        tsaPrecheckNumber: data.tsaPrecheckNumber,
        scrubSizeTop: data.scrubSizeTop,
        scrubSizeBottom: data.scrubSizeBottom,
        weight: data.weight,
        baseLocationId: data.baseLocationId,
        locationPreference: data.locationPreference,
        closestAirportForDeployment: data.closestAirportForDeploymentId
          ? Number(data.closestAirportForDeploymentId)
          : null,
      };

      const updatedLocation = {
        ...locationValues,
        state: countryHasStates ? state : null,
        timezone: homeLocationTimezone || '',
      };

      let homeLocationId = userData.employee.homeLocation?.id;
      let isLocationChanged = false;
      if (userData.employee.homeLocation) {
        const locationChangedValues = getChangedValues(updatedLocation, userData.employee.homeLocation);
        isLocationChanged = Object.keys(locationChangedValues).length > 0;
      }

      if (isLocationChanged || !userData.employee.homeLocation) {
        homeLocationId = await setHomeLocation(updatedLocation);
      }
      const changedValues = getChangedValues<Partial<EmployeeFormValues>>(updateBody, defaultValues);
      await mutateAsync({
        homeLocation: homeLocationId,
        ...changedValues,
      });

      toast.success(`Profile updated successfully.`);
      handleFormState();
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    }
  };

  if (isGeoDataLoading) return null;

  return (
    <Form<ProfileFormType> mode="onChange" defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
      <div className="p-6 flex flex-col gap-4 grow">
        {hasUpdatePermission && <ProfileFormActions isReadOnly={isReadOnly} handleFormState={handleFormState} />}
        <Profile data={userData.employee} isReadOnly={isReadOnly} />
      </div>
    </Form>
  );
};
